import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { toast } from 'sonner';
import { Loader2, Undo2, CheckSquare } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { createReturn, getBatches } from '../lib/api';

const DISPOSITIONS = [
  { value: 'main_store', label: 'Main Store' },
  { value: 'shop', label: 'Shop Fridge' },
  { value: 'waste', label: 'Waste / Disposed' },
];

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Record a formal Stock Return against a dispatch (event OR customer).
 * Creates a numbered RTN record + printable Return Note PDF on the backend.
 */
export const RecordReturnDialog = ({ dispatch, open, onOpenChange, onSuccess }) => {
  const [rows, setRows] = useState([]);
  const [reason, setReason] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [saving, setSaving] = useState(false);
  // Known batch codes per product — suggestions when backfilling a legacy
  // line that was dispatched without a batch.
  const [batchSuggestions, setBatchSuggestions] = useState({});

  useEffect(() => {
    if (open && dispatch) {
      setRows((dispatch.lines || []).map((ln, idx) => ({
        line_index: idx,
        product_id: ln.product_id || null,
        product_name: ln.product_name || 'Item',
        batch_code: ln.batch_code || '',
        entered_batch: '',
        is_adhoc: !!ln.is_adhoc,
        unit_type: ln.unit_type || 'units',
        dispatched: num(ln.quantity),
        already_returned: num(ln.quantity_returned),
        line_cases: num(ln.cases !== null && ln.cases !== undefined ? ln.cases : (ln.cases_override || ln.calculated_cases || 0)),
        line_weight: num(ln.weight_override || ln.total_weight || ln.estimated_weight || 0),
        already_cases: num(ln.cases_returned),
        already_weight: num(ln.weight_returned_kg),
        qty: '',
        cases: '',
        weight: '',
        casesTouched: false,
        weightTouched: false,
        disposition: 'main_store',
      })));
      setReason('');
      setReturnDate(new Date().toISOString().slice(0, 10));
      setReceivedBy('');

      // Fetch batch-code suggestions for products on no-batch lines
      const needy = [...new Set(
        (dispatch.lines || [])
          .filter((ln) => !ln.is_adhoc && ln.product_id && !ln.batch_code)
          .map((ln) => ln.product_id)
      )];
      needy.forEach(async (pid) => {
        try {
          const res = await getBatches({ product_id: pid });
          const list = Array.isArray(res.data) ? res.data : (res.data.batches || []);
          const codes = [...new Set(list.map((b) => b.batch_code).filter(Boolean))];
          setBatchSuggestions((prev) => ({ ...prev, [pid]: codes }));
        } catch { /* suggestions are optional */ }
      });
    }
  }, [open, dispatch]);

  const updateRow = (idx, patch) =>
    setRows((prev) => prev.map((r) => (r.line_index === idx ? { ...r, ...patch } : r)));

  const remainingOf = (r) => Math.max(r.dispatched - r.already_returned, 0);
  const fmtQty = (v) => String(Math.round(v * 1000) / 1000);

  // Prorate cases/weight from the returned qty (1a): suggested figures only —
  // manual entries are NEVER overwritten (touched flags). If the qty closes
  // the line, close out cases/weight exactly — but only when earlier returns
  // actually consumed cases/weight (old records without them prorate instead).
  const prorated = (r, qtyStr) => {
    if (qtyStr === '' || !(r.line_cases > 0 || r.line_weight > 0)) return { cases: '', weight: '' };
    const q = num(qtyStr);
    const remCases = Math.max(r.line_cases - r.already_cases, 0);
    const remWeight = Math.max(r.line_weight - r.already_weight, 0);
    const closes = Math.abs(r.already_returned + q - r.dispatched) < 1e-9;
    const closeCases = closes && (r.already_returned === 0 || r.already_cases > 0);
    const closeWeight = closes && (r.already_returned === 0 || r.already_weight > 0);
    const ratio = r.dispatched > 0 ? q / r.dispatched : 0;
    return {
      cases: r.line_cases > 0 ? fmtQty(closeCases ? remCases : Math.min(Math.round(r.line_cases * ratio * 100) / 100, remCases)) : '',
      weight: r.line_weight > 0 ? fmtQty(closeWeight ? remWeight : Math.min(Math.round(r.line_weight * ratio * 100) / 100, remWeight)) : '',
    };
  };

  const setQty = (r, qtyStr) => {
    const sug = prorated(r, qtyStr);
    updateRow(r.line_index, {
      qty: qtyStr,
      ...(qtyStr === ''
        ? { cases: '', weight: '', casesTouched: false, weightTouched: false }
        : {
            ...(r.casesTouched ? {} : { cases: sug.cases }),
            ...(r.weightTouched ? {} : { weight: sug.weight }),
          }),
    });
  };

  const selectAllRemaining = () =>
    setRows((prev) => prev.map((r) => {
      const rem = remainingOf(r);
      if (rem <= 0) return r;
      const qtyStr = fmtQty(rem);
      const sug = prorated(r, qtyStr);
      return {
        ...r,
        qty: qtyStr,
        ...(r.casesTouched ? {} : { cases: sug.cases }),
        ...(r.weightTouched ? {} : { weight: sug.weight }),
      };
    }));

  // Live over-return validation: the backend rejects these too, but the form
  // must flag them immediately and block submission.
  const remCasesOf = (r) => Math.max(r.line_cases - r.already_cases, 0);
  const remWeightOf = (r) => Math.max(r.line_weight - r.already_weight, 0);
  const rowErrors = (r) => {
    const errs = [];
    if (num(r.qty) > remainingOf(r) + 1e-9) errs.push(`quantity exceeds the ${fmtQty(remainingOf(r))} remaining`);
    if (num(r.qty) > 0 && num(r.cases) > remCasesOf(r) + 1e-6) errs.push(`cases exceed the ${fmtQty(remCasesOf(r))} remaining`);
    if (num(r.qty) > 0 && num(r.weight) > remWeightOf(r) + 1e-6) errs.push(`weight exceeds the ${fmtQty(remWeightOf(r))} kg remaining`);
    return errs;
  };
  const firstError = rows.map((r) => (rowErrors(r)[0] ? `${r.product_name}: ${rowErrors(r)[0]}` : null)).find(Boolean);

  const totalToReturn = rows.reduce((s, r) => s + num(r.qty), 0);
  const totalCases = rows.reduce((s, r) => s + (num(r.qty) > 0 ? num(r.cases) : 0), 0);
  const totalWeight = rows.reduce((s, r) => s + (num(r.qty) > 0 ? num(r.weight) : 0), 0);

  const handleSubmit = async () => {
    if (firstError) {
      toast.error(firstError);
      return;
    }
    const lines = rows
      .filter((r) => num(r.qty) > 0)
      .map((r) => ({
        line_index: r.line_index,
        quantity_returned: num(r.qty),
        disposition: r.disposition,
        ...(!r.batch_code && !r.is_adhoc && r.entered_batch.trim()
          ? { batch_code: r.entered_batch.trim().toUpperCase() }
          : {}),
      }));
    if (lines.length === 0) {
      toast.error('Enter a returned quantity on at least one line.');
      return;
    }
    for (const r of rows) {
      const q = num(r.qty);
      if (q > 0 && q > r.dispatched - r.already_returned + 1e-9) {
        toast.error(`${r.product_name}: cannot return more than the remaining ${(r.dispatched - r.already_returned).toFixed(2)}.`);
        return;
      }
    }
    if (reason.trim().length < 5) {
      toast.error('Enter a reason (min 5 characters) — required for the audit record.');
      return;
    }
    setSaving(true);
    try {
      const res = await createReturn({
        dispatch_id: dispatch.id,
        lines,
        reason: reason.trim(),
        return_date: returnDate || null,
        received_by: receivedBy.trim() || null,
      });
      toast.success(`Return ${res.data.return_number} recorded — stock returned to store.`);
      if (res.data.warning) {
        toast.warning(res.data.warning, { duration: 8000 });
      }
      onOpenChange(false);
      onSuccess?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record return');
    } finally {
      setSaving(false);
    }
  };

  if (!dispatch) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" data-testid="record-return-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="w-5 h-5 text-amber-600" />
            Record Stock Return — Dispatch #{dispatch.dispatch_number}
          </DialogTitle>
          <DialogDescription>
            Formal record of stock returning to the premises (main store) before any re-dispatch.
            A numbered Return Note PDF is generated for the Revenue audit trail.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Tick <strong>Full</strong> to return a whole line in one click — each line is one batch, so
              the batch follows automatically. Cases and weight pre-fill in proportion to the quantity;
              overtype them with the actual weighed figures if they differ.
            </p>
            <Button variant="outline" size="sm" onClick={selectAllRemaining} data-testid="return-select-all-btn">
              <CheckSquare className="w-3.5 h-3.5 mr-1.5" /> Return all remaining
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Batch</TableHead>
                <TableHead className="text-right">Dispatched</TableHead>
                <TableHead className="text-right">Prev. Returned</TableHead>
                <TableHead className="text-center w-12">Full</TableHead>
                <TableHead className="text-right w-24">Return Now</TableHead>
                <TableHead className="text-right w-20">Cases</TableHead>
                <TableHead className="text-right w-24">Wt (kg)</TableHead>
                <TableHead className="w-32">Goes To</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const remaining = remainingOf(r);
                const fullChecked = remaining > 0 && Math.abs(num(r.qty) - remaining) < 1e-9;
                return (
                  <TableRow key={r.line_index} data-testid={`return-line-${r.line_index}`}>
                    <TableCell>
                      <div className="font-medium text-sm">{r.product_name}</div>
                    </TableCell>
                    <TableCell>
                      {r.batch_code ? (
                        <span className="font-mono text-xs font-semibold text-blue-700" data-testid={`return-batch-${r.line_index}`}>
                          {r.batch_code}
                        </span>
                      ) : r.is_adhoc ? (
                        <span className="text-xs text-muted-foreground italic">freeline — no batch</span>
                      ) : (
                        <div>
                          <Input
                            list={`batch-suggestions-${r.line_index}`}
                            className="h-8 w-28 font-mono text-xs uppercase"
                            placeholder="enter batch"
                            maxLength={40}
                            value={r.entered_batch}
                            onChange={(e) => updateRow(r.line_index, { entered_batch: e.target.value })}
                            data-testid={`return-batch-input-${r.line_index}`}
                          />
                          <datalist id={`batch-suggestions-${r.line_index}`}>
                            {(batchSuggestions[r.product_id] || []).map((c) => (
                              <option key={c} value={c} />
                            ))}
                          </datalist>
                          <span className="text-[10px] text-amber-600 block mt-0.5">
                            no batch on line — enter it to fix the trace
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm">{r.dispatched.toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {r.already_returned > 0 ? r.already_returned.toFixed(2) : '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Checkbox
                        checked={fullChecked}
                        disabled={remaining <= 0}
                        onCheckedChange={(c) => setQty(r, c ? fmtQty(remaining) : '')}
                        data-testid={`return-full-${r.line_index}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number" min="0" max={remaining} step="any"
                        className={`h-8 text-right${num(r.qty) > remaining + 1e-9 ? ' border-red-500 focus-visible:ring-red-500 text-red-600' : ''}`}
                        placeholder={remaining > 0 ? `max ${remaining.toFixed(2)}` : 'none left'}
                        value={r.qty}
                        disabled={remaining <= 0}
                        onChange={(e) => setQty(r, e.target.value)}
                        title={num(r.qty) > remaining + 1e-9 ? `Only ${fmtQty(remaining)} left to return` : undefined}
                        data-testid={`return-qty-${r.line_index}`}
                      />
                    </TableCell>
                    <TableCell>
                      {r.line_cases > 0 ? (
                        <Input
                          type="number" min="0" max={remCasesOf(r)} step="any"
                          className={`h-8 text-right${num(r.qty) > 0 && num(r.cases) > remCasesOf(r) + 1e-6 ? ' border-red-500 focus-visible:ring-red-500 text-red-600' : ''}`}
                          value={r.cases}
                          disabled={remaining <= 0 || num(r.qty) <= 0}
                          onChange={(e) => updateRow(r.line_index, { cases: e.target.value, casesTouched: true })}
                          title={num(r.qty) > 0 && num(r.cases) > remCasesOf(r) + 1e-6 ? `Only ${fmtQty(remCasesOf(r))} case(s) left to return` : undefined}
                          data-testid={`return-cases-${r.line_index}`}
                        />
                      ) : (
                        <span className="block text-right text-xs text-muted-foreground pr-1">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.line_weight > 0 ? (
                        <Input
                          type="number" min="0" max={remWeightOf(r)} step="any"
                          className={`h-8 text-right${num(r.qty) > 0 && num(r.weight) > remWeightOf(r) + 1e-6 ? ' border-red-500 focus-visible:ring-red-500 text-red-600' : ''}`}
                          value={r.weight}
                          disabled={remaining <= 0 || num(r.qty) <= 0}
                          onChange={(e) => updateRow(r.line_index, { weight: e.target.value, weightTouched: true })}
                          title={num(r.qty) > 0 && num(r.weight) > remWeightOf(r) + 1e-6 ? `Only ${fmtQty(remWeightOf(r))} kg left to return` : undefined}
                          data-testid={`return-weight-${r.line_index}`}
                        />
                      ) : (
                        <span className="block text-right text-xs text-muted-foreground pr-1">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={r.disposition}
                        onValueChange={(v) => updateRow(r.line_index, { disposition: v })}
                        disabled={remaining <= 0}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`return-disposition-${r.line_index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DISPOSITIONS.map((d) => (
                            <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="return-date">Return Date</Label>
              <Input
                id="return-date" type="date" value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                data-testid="return-date-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="received-by">Received Into Store By</Label>
              <Input
                id="received-by" value={receivedBy} maxLength={120}
                onChange={(e) => setReceivedBy(e.target.value)}
                placeholder="Name of the person receiving the stock"
                data-testid="return-received-by-input"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="return-reason">Reason *</Label>
            <Textarea
              id="return-reason" rows={2} value={reason} maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Unused stock returned from BLOOM trade show"
              data-testid="return-reason-input"
            />
          </div>
        </div>

        <DialogFooter className="items-center sm:justify-between">
          <span className="text-sm text-muted-foreground" data-testid="return-total">
            Returning: <strong>{totalToReturn.toFixed(2)}</strong> across{' '}
            {rows.filter((r) => num(r.qty) > 0).length} line(s)
            {(totalCases > 0 || totalWeight > 0) && (
              <span className="ml-1">
                · <strong>{totalCases.toFixed(2)}</strong> cases · <strong>{totalWeight.toFixed(2)}</strong> kg
              </span>
            )}
          </span>
          <div className="flex flex-col items-end gap-1">
            {firstError && (
              <span className="text-xs text-red-600 font-medium" data-testid="return-validation-error">{firstError}</span>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={saving || !!firstError} data-testid="confirm-return-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Record Return
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecordReturnDialog;
